import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Loader2, Calendar, MapPin, Users, ArrowRight, Ticket, Sparkles,
  Facebook, Linkedin, Globe, Navigation, PlayCircle,
} from 'lucide-react';
import api from '../../lib/api';
import { LOGO_DARK_BG } from '../../lib/constants';
import ShareMenu from '../../components/ShareMenu';
import CnibMap from '../../components/CnibMap';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

function formatRange(start, end) {
  try {
    const s = new Date(start); const e = new Date(end);
    const sameDay = s.toDateString() === e.toDateString();
    const opts = { day: '2-digit', month: 'long', year: 'numeric' };
    return sameDay
      ? s.toLocaleDateString('es-BO', opts)
      : `${s.toLocaleDateString('es-BO', { day: '2-digit', month: 'short' })} — ${e.toLocaleDateString('es-BO', opts)}`;
  } catch { return ''; }
}

function Countdown({ target, color }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(i);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - Date.now());
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const started = diff === 0;
  const box = (n, l) => (
    <div className="flex flex-col items-center">
      <div className="rounded-2xl px-3 py-2 text-center ring-1 ring-white/15 backdrop-blur min-w-[62px]" style={{ background: 'rgba(255,255,255,0.08)' }}>
        <p className="font-heading text-2xl font-black leading-none" style={{ color }}>{String(n).padStart(2, '0')}</p>
      </div>
      <p className="mt-1 text-[9px] font-bold uppercase tracking-wider text-slate-300">{l}</p>
    </div>
  );
  // Suppress unused tick warning
  void tick;
  if (started) {
    return <p className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold" style={{ background: `${color}20`, color }}>¡El evento ya comenzó!</p>;
  }
  return (
    <div className="flex gap-2" data-testid="evento-countdown">
      {box(days, 'días')}{box(hours, 'horas')}{box(mins, 'min')}{box(secs, 'seg')}
    </div>
  );
}

export default function EventoMicrositio() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [registerForm, setRegisterForm] = useState({ tier_id: '', holder_name: '', holder_email: '', holder_whatsapp: '' });
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);

  useEffect(() => {
    api.get(`/events/${slug}`)
      .then(({ data }) => { setEvent(data); setRegisterForm((f) => ({ ...f, tier_id: data.tiers?.[0]?.tier_id || '' })); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (user) setRegisterForm((f) => ({ ...f, holder_name: user.name || f.holder_name, holder_email: user.email || f.holder_email }));
  }, [user]);

  const register = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/events/${slug}/register`, registerForm);
      setTicket(data);
      toast.success(data.is_free ? '¡Registro confirmado! Revisa tu correo o descarga tu QR.' : 'Registro iniciado. Pendiente de pago.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo registrar.');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-slate-900"><Loader2 className="h-10 w-10 animate-spin text-orange-500" /></div>;
  if (notFound || !event) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-900 p-6 text-center">
      <h1 className="font-heading text-3xl font-bold text-white">Evento no encontrado</h1>
      <Link to="/eventos" className="rounded-full bg-orange-500 px-6 py-3 font-bold text-white">Ver todos los eventos</Link>
    </div>
  );

  const primary = event.primary_color || '#F97316';
  const accent = event.accent_color || '#0F4C81';
  const dateStr = formatRange(event.starts_at, event.ends_at);
  const agendaLines = (event.agenda || '').split('\n').filter((l) => l.trim());
  const highlights = Array.isArray(event.highlights) ? event.highlights : [];
  const speakers = Array.isArray(event.speakers) ? event.speakers : [];
  const gallery = Array.isArray(event.gallery) ? event.gallery : [];
  const tiers = Array.isArray(event.tiers) ? event.tiers : [];
  const sponsors = (Array.isArray(event.sponsors) ? event.sponsors : [])
    .map((s) => (typeof s === 'string' ? { name: s } : s))
    .filter((s) => s && (s.name || s.logo));
  const sponsorsByTier = sponsors.reduce((acc, s) => {
    const t = s.tier || 'Auspiciadores';
    (acc[t] = acc[t] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="evento-micrositio" style={{ ['--primary']: primary, ['--accent']: accent }}>
      {/* HERO */}
      <section className="relative overflow-hidden pb-24 pt-6">
        <div className="absolute inset-0">
          {event.cover_image ? (
            <img src={event.cover_image} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/70 to-slate-900/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-slate-900/60" />
        </div>

        <div className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/eventos" className="flex items-center gap-2">
            <img src={LOGO_DARK_BG} alt="CNIB" className="h-9 w-auto" />
          </Link>
          <ShareMenu title={`${event.title} — ${event.city}`} url={typeof window !== 'undefined' ? window.location.href : ''} compact label="Compartir" testid="evento-share" />
        </div>

        <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-10 px-4 pt-10 sm:px-6 md:pt-20 lg:grid-cols-5 lg:px-8">
          <div className="lg:col-span-3">
            {event.hashtag && (
              <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em]"
                style={{ borderColor: `${primary}80`, background: `${primary}20`, color: primary }}>
                <Sparkles className="h-3 w-3" /> {event.hashtag}
              </motion.p>
            )}
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-4 font-heading text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl" data-testid="evento-title">
              {event.title}
            </motion.h1>
            {event.subtitle && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-4 text-lg text-slate-300 lg:text-xl">
                {event.subtitle}
              </motion.p>
            )}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              className="mt-6 flex flex-wrap gap-3 text-sm text-slate-200">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 backdrop-blur">
                <Calendar className="h-4 w-4" style={{ color: primary }} /> {dateStr}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 backdrop-blur">
                <MapPin className="h-4 w-4" style={{ color: primary }} /> {event.venue}, {event.city}
              </span>
              {speakers.length > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 backdrop-blur">
                  <Users className="h-4 w-4" style={{ color: primary }} /> {speakers.length} speakers
                </span>
              )}
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-7">
              <Countdown target={event.starts_at} color={primary} />
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => setShowRegister(true)} data-testid="evento-cta" className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold shadow-xl transition-all hover:-translate-y-0.5"
                style={{ background: primary, color: '#fff', boxShadow: `0 10px 40px -10px ${primary}` }}>
                <Ticket className="h-4 w-4" />
                {event.cta_text || 'Reservar mi lugar'}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              {event.stream_url && (
                <a href={event.stream_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-6 py-3 text-sm font-bold backdrop-blur hover:bg-white/20">
                  <PlayCircle className="h-4 w-4" /> Ver stream
                </a>
              )}
            </motion.div>
          </div>

          {/* Tickets card */}
          {tiers.length > 0 && (
            <motion.aside initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.4 }} className="lg:col-span-2">
              <div className="relative overflow-hidden rounded-3xl bg-white/10 p-5 backdrop-blur-xl ring-1 ring-white/20">
                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: primary }}>Entradas disponibles</p>
                <div className="mt-3 space-y-2">
                  {tiers.map((t) => (
                    <div key={t.tier_id} className="flex items-center justify-between rounded-xl bg-white/10 p-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold">{t.name}</p>
                        <p className="text-[11px] text-slate-300">Quedan {t.available}</p>
                      </div>
                      <p className="font-heading text-base font-extrabold">
                        {t.is_free ? 'GRATIS' : `US$ ${t.price_usd}`}
                      </p>
                    </div>
                  ))}
                </div>
                <button onClick={() => setShowRegister(true)} className="mt-4 w-full rounded-full py-2.5 text-sm font-bold text-white" style={{ background: primary }}>
                  Comprar entradas
                </button>
              </div>
            </motion.aside>
          )}
        </div>
      </section>

      {/* HIGHLIGHTS */}
      {highlights.length > 0 && (
        <section className="bg-slate-800/40 py-14">
          <div className="mx-auto grid max-w-6xl gap-6 px-4 sm:px-6 md:grid-cols-3">
            {highlights.map((h, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="rounded-2xl bg-white/5 p-5 ring-1 ring-white/10">
                <Sparkles className="h-5 w-5" style={{ color: primary }} />
                <p className="mt-3 text-sm leading-relaxed text-slate-200">{h}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* DESCRIPTION */}
      {event.description && (
        <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6" data-testid="evento-description">
          <h2 className="font-heading text-3xl font-extrabold">Acerca del evento</h2>
          <div
            className="prose prose-invert prose-sm sm:prose-base mt-5 max-w-none text-slate-300"
            dangerouslySetInnerHTML={{ __html: event.description }}
          />
        </section>
      )}

      {/* SPEAKERS */}
      {speakers.length > 0 && (
        <section className="bg-slate-800/40 py-16" data-testid="evento-speakers">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <h2 className="font-heading text-3xl font-extrabold">Speakers destacados</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {speakers.map((s, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-50px' }} transition={{ delay: i * 0.08 }}
                  className="group overflow-hidden rounded-3xl bg-white/5 ring-1 ring-white/10 transition-all hover:-translate-y-1 hover:ring-white/20">
                  <div className="aspect-square overflow-hidden">
                    {s.photo ? (
                      <img src={s.photo} alt={s.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-5xl font-black" style={{ background: `linear-gradient(135deg, ${primary}40, ${accent}40)` }}>
                        {s.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <p className="font-heading text-lg font-extrabold">{s.name}</p>
                    {s.role && <p className="text-sm" style={{ color: primary }}>{s.role}</p>}
                    {s.bio && <p className="mt-2 line-clamp-3 text-xs text-slate-300">{s.bio}</p>}
                    {s.linkedin && (
                      <a href={s.linkedin} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-white">
                        <Linkedin className="h-3 w-3" /> LinkedIn
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AGENDA (estructurada por día) */}
      {agendaLines.length > 0 && (() => {
        const groups = {};
        agendaLines.forEach((line) => {
          const m = line.match(/^(d[ií]a\s*\d+|\w+\s*\d{1,2})\s*[-·:]?\s*(?:(\d{1,2}:\d{2})\s*[-·]?\s*)?(.*)$/i);
          const day = m?.[1] || 'Programa';
          const time = m?.[2] || null;
          const text = m?.[3]?.trim() || line.trim();
          const key = day.trim();
          if (!groups[key]) groups[key] = [];
          groups[key].push({ time, text, raw: line });
        });
        const days = Object.keys(groups);
        return (
          <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6" data-testid="evento-agenda">
            <h2 className="font-heading text-3xl font-extrabold">Programa completo</h2>
            <p className="mt-2 text-slate-400">Agenda dinámica con horarios y expositores.</p>
            <div className="mt-8 space-y-8">
              {days.map((day, di) => (
                <motion.div key={day} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-50px' }} transition={{ delay: di * 0.1 }}
                  className="relative overflow-hidden rounded-3xl bg-white/5 p-6 ring-1 ring-white/10">
                  <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: primary }}>{day}</p>
                  <div className="mt-4 space-y-1" data-testid={`evento-agenda-day-${di}`}>
                    {groups[day].map((item, j) => (
                      <div key={j} className="flex items-start gap-4 border-l-2 py-2 pl-4" style={{ borderColor: `${primary}40` }}>
                        {item.time ? (
                          <span className="w-16 shrink-0 rounded-lg px-2 py-1 text-center font-heading text-sm font-black" style={{ background: `${primary}25`, color: primary }}>{item.time}</span>
                        ) : (
                          <span className="w-16 shrink-0 text-center text-xs text-slate-500">—</span>
                        )}
                        <p className="flex-1 text-sm leading-relaxed text-slate-200">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        );
      })()}

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="bg-slate-800/40 py-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <h2 className="font-heading text-3xl font-extrabold">Galería</h2>
            <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {gallery.map((url, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  className="group aspect-square overflow-hidden rounded-xl">
                  <img src={url} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MAP */}
      {event.lat != null && event.lng != null && (
        <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-heading text-3xl font-extrabold">Ubicación</h2>
              <p className="mt-1 text-slate-300">{event.venue}{event.address ? `, ${event.address}` : ''}, {event.city}</p>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="evento-location-actions">
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${event.lat},${event.lng}`} target="_blank" rel="noopener noreferrer" data-testid="evento-directions" className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white" style={{ background: primary }}>
                <Navigation className="h-3.5 w-3.5" /> Cómo llegar
              </a>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(
                  `📍 Ubicación del evento "${event.title}"\nhttps://www.google.com/maps/search/?api=1&query=${event.lat},${event.lng}`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="evento-share-location"
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-600"
              >
                <MapPin className="h-3.5 w-3.5" /> Compartir ubicación
              </a>
              <span data-testid="evento-share-event-wrapper">
                <ShareMenu title={`${event.title} — ${event.city}`} url={typeof window !== 'undefined' ? window.location.href : ''} compact label="Compartir evento" testid="evento-share-event" />
              </span>
            </div>
          </div>
          <div className="mt-5 overflow-hidden rounded-3xl ring-1 ring-white/10">
            <CnibMap
              center={[event.lng, event.lat]}
              zoom={14}
              markers={[{ id: event.event_id, lat: event.lat, lng: event.lng, color: primary, icon: 'dot', size: 'lg' }]}
              className="h-80 w-full"
            />
          </div>
        </section>
      )}

      {/* SPONSORS */}
      {sponsors.length > 0 && (
        <section className="border-t border-slate-800/60 bg-slate-900/60 py-16" data-testid="evento-sponsors">
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: primary }}>Con el apoyo de</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold">Auspiciadores y partners</h2>
            <div className="mt-8 space-y-10">
              {Object.entries(sponsorsByTier).map(([tier, list]) => (
                <div key={tier}>
                  <h3 className="mb-5 text-[11px] font-bold uppercase tracking-[0.25em] text-slate-400">{tier}</h3>
                  <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                    {list.map((s, i) => {
                      const content = (
                        <div className="flex h-28 flex-col items-center justify-center gap-2 rounded-2xl bg-white/95 p-4 ring-1 ring-white/10 transition hover:-translate-y-0.5 hover:ring-white/40">
                          {s.logo ? (
                            <img src={s.logo} alt={s.name || 'Auspiciador'} className="max-h-16 w-auto max-w-full object-contain" loading="lazy" />
                          ) : (
                            <span className="text-center text-sm font-bold text-slate-800">{s.name}</span>
                          )}
                          {s.logo && s.name ? (
                            <span className="text-center text-[10px] font-semibold uppercase tracking-wide text-slate-500">{s.name}</span>
                          ) : null}
                        </div>
                      );
                      return s.url ? (
                        <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`evento-sponsor-${i}`}>
                          {content}
                        </a>
                      ) : (
                        <div key={i} data-testid={`evento-sponsor-${i}`}>{content}</div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {event.faq && (
        <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
          <h2 className="font-heading text-3xl font-extrabold">Preguntas frecuentes</h2>
          <p className="mt-5 whitespace-pre-line text-base text-slate-300">{event.faq}</p>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-16" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }}>
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-4xl font-extrabold">¿Listo para ser parte?</h2>
          <p className="mt-3 text-white/90">Asegura tu lugar antes de que se agoten las entradas.</p>
          <button onClick={() => setShowRegister(true)} className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold shadow-xl" style={{ color: accent }}>
            <Ticket className="h-4 w-4" /> {event.cta_text || 'Reservar mi lugar'}
          </button>
        </div>
      </section>

      <footer className="py-8 text-center text-xs text-slate-500">
        Organiza: {event.organizer} · Micrositio generado en CNIB Connect
      </footer>

      {/* Register modal */}
      {showRegister && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-900/80 p-0 sm:items-center sm:p-6" onClick={() => !submitting && setShowRegister(false)} data-testid="evento-register-modal">
          <div className="w-full max-w-md overflow-y-auto rounded-t-3xl bg-white p-6 text-slate-900 sm:rounded-3xl" style={{ maxHeight: '92vh' }} onClick={(e) => e.stopPropagation()}>
            {ticket ? (
              <div className="text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full" style={{ background: `${primary}20` }}>
                  <Ticket className="h-7 w-7" style={{ color: primary }} />
                </div>
                <h3 className="mt-4 font-heading text-2xl font-extrabold">¡Registro confirmado!</h3>
                <p className="mt-2 text-sm text-slate-600">
                  {ticket.is_free
                    ? 'Tu entrada ya está lista. Ve a Mis Tickets para descargar tu QR.'
                    : 'Reserva creada. Un asesor te contactará para completar el pago.'}
                </p>
                <Link to="/app" className="mt-5 inline-block rounded-full px-6 py-2.5 text-sm font-bold text-white" style={{ background: primary }}>Ir a mi cuenta</Link>
                <button onClick={() => setShowRegister(false)} className="mt-2 block w-full text-xs font-bold text-slate-500">Cerrar</button>
              </div>
            ) : (
              <form onSubmit={register} className="space-y-3">
                <h3 className="font-heading text-xl font-extrabold">Reservar entrada</h3>
                <select required value={registerForm.tier_id} onChange={(e) => setRegisterForm({ ...registerForm, tier_id: e.target.value })} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm" data-testid="evento-register-tier">
                  <option value="">Elegir entrada</option>
                  {tiers.map((t) => (
                    <option key={t.tier_id} value={t.tier_id} disabled={t.available <= 0}>
                      {t.name} — {t.is_free ? 'GRATIS' : `US$ ${t.price_usd}`} {t.available <= 0 ? '(agotada)' : ''}
                    </option>
                  ))}
                </select>
                <input required placeholder="Nombre completo" value={registerForm.holder_name} onChange={(e) => setRegisterForm({ ...registerForm, holder_name: e.target.value })} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm" data-testid="evento-register-name" />
                <input required type="email" placeholder="Correo" value={registerForm.holder_email} onChange={(e) => setRegisterForm({ ...registerForm, holder_email: e.target.value })} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm" data-testid="evento-register-email" />
                <input placeholder="WhatsApp (opcional)" value={registerForm.holder_whatsapp} onChange={(e) => setRegisterForm({ ...registerForm, holder_whatsapp: e.target.value })} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm" />
                <button type="submit" disabled={submitting} className="w-full rounded-full py-2.5 text-sm font-bold text-white disabled:opacity-60" style={{ background: primary }} data-testid="evento-register-submit">
                  {submitting ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : 'Confirmar registro'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
